"use client";

import * as React from "react";
import { Layers, Box, CircleDashed, ArrowDownToLine, FlaskConical } from "lucide-react";
import { cn } from "@/lib/utils";
import { useRevealOnScroll } from "@/hooks/useRevealOnScroll";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
const services = [{
  title: "Soil Compaction Testing",
  description: "Nuclear gauge density testing and sand cone methods to verify optimal moisture-density relationships.",
  icon: Layers
}, {
  title: "Concrete Sampling",
  description: "Fresh concrete testing including slump, air content, and casting of cylinders for compressive strength verification.",
  icon: Box
}, {
  title: "Asphalt Core Extraction",
  description: "Precision coring to determine pavement thickness, density, and composition for quality assurance.",
  icon: CircleDashed
}, {
  title: "Bearing Capacity",
  description: "Dynamic cone penetrometer (DCP) and plate load testing to assess subgrade strength and foundation suitability.",
  icon: ArrowDownToLine
}, {
  title: "Environmental Sampling",
  description: "Soil and groundwater extraction for contamination analysis and environmental site assessments.",
  icon: FlaskConical
}];
export default function FieldTesting() {
  const {
    ref: headerRef,
    isVisible: isHeaderVisible
  } = useRevealOnScroll({
    threshold: 0.1
  });
  return <section id="fieldtesting" className="bg-background text-foreground py-24 px-4 md:px-8 overflow-hidden">
      <div className="max-w-7xl mx-auto">
        <div ref={headerRef} className={cn("text-center mb-16 transition-all duration-700 ease-out", isHeaderVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-8")}>
          <h2 className="font-sans uppercase tracking-wider text-3xl md:text-4xl font-bold mb-4 text-foreground">
            Field Testing & Analysis
          </h2>
          <p className="font-sans text-muted-foreground max-w-2xl mx-auto text-lg">
            Precision on-site diagnostics and material evaluation to ensure structural integrity and compliance with rigorous engineering standards.
          </p>
        </div>

        <div className="grid grid-cols-1 md:grid-cols-12 gap-6">
          {services.map((service, i) => <ServiceCard key={i} service={service} index={i} />)}
        </div>
      </div>
    </section>;
}
function ServiceCard({
  service,
  index
}: {
  service: typeof services[0];
  index: number;
}) {
  const {
    ref,
    isVisible
  } = useRevealOnScroll({
    threshold: 0.2
  });
  const Icon = service.icon;
  return <div ref={ref} data-index={index} className={cn("md:col-span-6 lg:col-span-4 transition-all duration-700 ease-out", index === 3 && "lg:col-start-3", index === 4 && "lg:col-start-7", isVisible ? "opacity-100 translate-y-0" : "opacity-0 translate-y-12")} style={{
    transitionDelay: `${index * 100}ms`
  }}>
      <Card className="h-full rounded-none border-border bg-card shadow-none hover:border-primary/50 transition-colors duration-300">
        <CardHeader className="pb-4">
          <div className="mb-4 inline-flex h-12 w-12 items-center justify-center bg-primary/10">
            <Icon className="size-6 text-primary" strokeWidth={1.5} />
          </div>
          <CardTitle className="font-sans text-xl tracking-tight">
            {service.title}
          </CardTitle>
        </CardHeader>
        <CardContent>
          <p className="text-muted-foreground font-sans leading-relaxed">
            {service.description}
          </p>
        </CardContent>
      </Card>
    </div>;
}